import React, { useEffect, useState } from 'react';
import { Users, Network, RefreshCw, ArrowRight, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const CHF = (n) => new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(n || 0);

const STATUS_LABEL = {
  registered: { text: 'Geworben (offen)', cls: 'bg-amber-100 text-amber-800' },
  rewarded: { text: 'Belohnt', cls: 'bg-green-100 text-green-800' },
};

export const ReferralOverviewCard = () => {
  const [tracking, setTracking] = useState(null);
  const [network, setNetwork] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, nRes] = await Promise.all([
        axios.get(`${API}/settings/referral/tracking`, authHeaders()),
        axios.get(`${API}/settings/friendship-tree/network-overview`, authHeaders()),
      ]);
      setTracking(tRes.data || []);
      setNetwork(nRes.data || { rows: [] });
    } catch (e) {
      toast.error('Empfehlungs-Übersicht konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const fmtUnits = (v, unit) => (unit === '%' ? `−${v}%` : `−${CHF(v)}`);
  const unit = network?.unit || '%';

  return (
    <Card className="border-teal-200" data-testid="referral-overview-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Network className="text-teal-600" /> Empfehlungs-Übersicht
            </CardTitle>
            <CardDescription>
              Wer hat wen geworben und welchen Freundeskreis-Rabatt hätte jeder Werber — inklusive
              der <strong>nächsten Reduktion</strong> bei einer weiteren aktiven Empfehlung. Reine Anzeige.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="referral-overview-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Nächste Reduktion je Werber */}
        <div className="space-y-2">
          <p className="text-sm font-semibold flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-teal-600" /> Rabatt & nächste Reduktion je Werber
          </p>
          {(!network || (network.rows || []).length === 0) ? (
            <p className="text-sm text-gray-400" data-testid="referral-overview-network-empty">
              Noch keine aktiven Empfehlungs-Netzwerke vorhanden.
            </p>
          ) : (
            <div className="border border-gray-100 rounded-lg divide-y max-h-80 overflow-auto">
              {network.rows.map((r) => (
                <div key={r.customer_id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`referral-overview-network-${r.customer_id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800 truncate">
                      {r.name}
                      {r.exempt && <span className="ml-2 text-[10px] text-amber-700">(Individualpreis)</span>}
                    </p>
                    <p className="text-[11px] text-gray-500">
                      {r.active_members_total}/{r.members_total} aktiv · {r.direct_active} direkte aktiv
                    </p>
                  </div>
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <div className="text-right">
                      <span className="font-semibold text-teal-700">{fmtUnits(r.current_units, r.unit)}</span>
                      {r.capped && <span className="ml-1 text-[10px] text-gray-400">(max)</span>}
                    </div>
                    {!r.capped && r.next_delta > 0 && (
                      <>
                        <ArrowRight className="w-3 h-3 text-gray-400" />
                        <div className="text-right">
                          <span className="font-semibold text-green-700">{fmtUnits(r.next_units, r.unit)}</span>
                          <span className="block text-[10px] text-green-600">nächste (+{r.unit === '%' ? `${r.next_delta}%` : CHF(r.next_delta)})</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Wer hat wen geworben */}
        <div className="space-y-2">
          <p className="text-sm font-semibold flex items-center gap-2">
            <Users className="w-4 h-4 text-teal-600" /> Wer hat wen geworben
          </p>
          {(!tracking || tracking.length === 0) ? (
            <p className="text-sm text-gray-400" data-testid="referral-overview-tracking-empty">
              Noch keine Empfehlungen erfasst.
            </p>
          ) : (
            <div className="border border-gray-100 rounded-lg divide-y max-h-80 overflow-auto">
              {tracking.map((e) => {
                const st = STATUS_LABEL[e.status] || { text: e.status, cls: 'bg-gray-100 text-gray-700' };
                return (
                  <div key={e.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`referral-overview-track-${e.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium text-gray-800 truncate">{e.referrer_name}</span>
                      <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700 truncate">{e.referred_name}</span>
                    </div>
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      {e.reward && <span className="text-[11px] text-gray-500">{e.reward}</span>}
                      <Badge className={`text-[10px] ${st.cls}`}>{st.text}</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
